'use client';
import { Button } from '@pickleballinc/react-ui';
import { useState } from 'react';

import { useSaveCompletedMatchScores } from '@/modules/league/new-components/change-scores-components/hooks';
import { PlayerInfo } from '@/modules/league/new-components/change-scores-components/PlayerInfo';
import { ScoreBox } from '@/modules/league/new-components/change-scores-components/ScoreBox';
import { CompletedMatch, GameDayInfo } from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';
import { initializeScoresArrayForEdit } from '@/utils/actions/leagues/gameDay/functions/getScores';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface ChangeScoresWrapperProps {
  headerContent: GameDayInfo;
  match: CompletedMatch;
  isAuthorized: boolean | null;
}

export const SingleMatch = ({ match, isAuthorized }: ChangeScoresWrapperProps) => {
  const [scoresArray, setScoresArray] = useState(initializeScoresArrayForEdit(match));
  const { setLoading } = useLoader();
  const queryClient = useQueryClient();
  const { mutate: saveCompletedMatchScores } = useSaveCompletedMatchScores();

  const saveScores = () => {
    const mutationData: { [key: string]: number } = {};

    for (let i = 1; i <= match?.MaxGames; i++) {
      mutationData[`Team_1_Game${i}Score`] = scoresArray[i - 1]?.team1Score ?? 0;
      mutationData[`Team_2_Game${i}Score`] = scoresArray[i - 1]?.team2Score ?? 0;
    }
    setLoading(true);
    saveCompletedMatchScores(
      { match, mutationData: mutationData },
      {
        onSuccess: (data) => {
          if (data.message) {
            toast.success(data.message);
          }
          queryClient.invalidateQueries(['league_change_scores']);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
          toast.error('Failed to update scores. Please try again.');
        },
      }
    );
  };

  return (
    <>
      <div className='group flex h-auto w-full flex-col gap-6 rounded-lg bg-white shadow-sm hover:bg-gray-50 md:max-w-[350px] lg:max-w-[410px]'>
        <div className='flex flex-col gap-2 p-2'>
          <h2 className='pr-4 text-left text-[16px] font-semibold leading-[22px] text-gray-900'>
            Pool {match.PoolNumber} - Match {match.DisplayMatchNumber}
          </h2>
          <div className='w-full border-t-[0.5px] border-gray-200 px-2 md:block' />
          <div className='flex flex-row'>
            <div className='flex w-full flex-col justify-between gap-1.5'>
              <PlayerInfo
                firstName={match.T1a_FirstName}
                lastName={match.T1a_LastName}
                subbedPlayer={match.T1a_SubbedPlayer}
              />
              <PlayerInfo
                firstName={match.T1b_FirstName}
                lastName={match.T1b_LastName}
                subbedPlayer={match.T1b_SubbedPlayer}
              />
            </div>

            <div className='flex flex-row items-center gap-2'>
              {[...Array(match?.MaxGames)].map((_, i) => (
                <ScoreBox
                  key={match?.T1a_UserID}
                  player={match}
                  indexOfPlayer={i}
                  teamNumber={1}
                  scoresArray={scoresArray}
                  setScoresArray={setScoresArray}
                  isAuthorized={isAuthorized}
                />
              ))}
            </div>
          </div>
          <div className='flex w-full flex-row items-center justify-center'>
            <div className='w-full border-t-[0.5px] border-gray-200 px-2 md:block' />
            <div className='px-4 text-xs uppercase text-gray-300'>vs</div>
            <div className='w-full border-t-[0.5px] border-gray-200 px-2 md:block' />
          </div>
          <div className='flex flex-row'>
            <div className='flex w-full flex-col justify-between gap-1.5'>
              <PlayerInfo
                firstName={match.T2a_FirstName}
                lastName={match.T2a_LastName}
                subbedPlayer={match.T2a_SubbedPlayer}
              />
              <PlayerInfo
                firstName={match.T2b_FirstName}
                lastName={match.T2b_LastName}
                subbedPlayer={match.T2b_SubbedPlayer}
              />
            </div>

            <div className='flex flex-row items-center gap-2'>
              {[...Array(match?.MaxGames)].map((_, i) => (
                <ScoreBox
                  key={match?.T2a_UserID}
                  player={match}
                  indexOfPlayer={i}
                  teamNumber={2}
                  scoresArray={scoresArray}
                  setScoresArray={setScoresArray}
                  isAuthorized={isAuthorized}
                />
              ))}
            </div>
          </div>
          {isAuthorized ? (
            <>
              <div className='flex items-end justify-end gap-4'>
                <Button
                  variant='primary'
                  className='flex !px-2 !py-1.5 !text-xs focus:!shadow-[unset] md:!text-sm !bg-leagues-300 hover:!bg-leagues-500'
                  onClick={() => {
                    saveScores();
                  }}
                >
                  Save Scores
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};
